import {
  fetchApps,
  fetchEnvironments,
  selectAppsByOrgAsList,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import { useCompositeLoader, useLoader, useSelector } from "@app/react";
import { ssoTokenUrl } from "@app/routes";
import { AppSidebarLayout } from "../layouts";
import {
  AnnouncementBanner,
  ButtonLink,
  ButtonLinkExternal,
  IconDownload,
  IconKey,
  Loading,
} from "../shared";
import { SignBaaBanner } from "../shared";
import {
  ActivityFeedCompact,
  LastDeploy,
  Overview,
  RecentlyViewedResources,
  Scaling,
  SecurityCompliance,
  SetupWidget,
} from "../shared/home";

const PLATFORM_IMPROVEMENTS_EXPIRY = new Date("2026-11-01T00:00:00Z").getTime();

export const HomePage = () => {
  const org = useSelector(selectOrganizationSelected);
  const apps = useSelector(selectAppsByOrgAsList);
  const appsLoader = useLoader(fetchApps);
  const pageLoader = useCompositeLoader([fetchApps, fetchEnvironments]);
  const showOnboardingOnly = appsLoader.isSuccess && apps.length === 0;

  if (!org.id) {
    return <Loading text="Loading..." />;
  }

  const newBadgeExpiry = new Date("2026-05-21T00:00:00Z").getTime();
  const showNewBadge = Date.now() < newBadgeExpiry;

  const homeHeader = (
    <div className="flex items-center justify-between border-b border-gray-200 pb-4">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-gray-900">Home</h1>
        {showNewBadge && (
          <span className="inline-flex items-center justify-center px-2 py-1 rounded text-xs font-semibold text-black uppercase bg-indigo-200 leading-none">
            New
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <ButtonLink to={ssoTokenUrl()} variant="white">
          <IconKey variant="sm" />
          <span className="pl-2">SSO CLI Token</span>
        </ButtonLink>
        <ButtonLinkExternal
          href="https://www.aptible.com/docs/reference/aptible-cli/overview"
          variant="white"
          className="gap-2"
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconDownload variant="sm" />
          Install the CLI
        </ButtonLinkExternal>
      </div>
    </div>
  );

  return (
    <AppSidebarLayout
      header={homeHeader}
      mainWrapperDivClassName="overflow-x-hidden"
    >
      <div className="space-y-8">
        <div className="space-y-6">
          <AnnouncementBanner
            dismissKey="platform-improvements-sept-2026"
            badge="SEP 2026"
            icon={<span>🚀</span>}
            title="New: Platform Improvements"
            description="Introducing Instant Rollbacks, upgraded instance generations, and additional fixes and improvements."
            link={{
              href: "https://www.aptible.com/changelog/platform-improvements-september-2026",
              label: "View changelog",
            }}
            expiresAt={PLATFORM_IMPROVEMENTS_EXPIRY}
          />
          <SignBaaBanner />
          <SetupWidget />
          <Overview />
          {!pageLoader.isInitialLoading && !showOnboardingOnly && org?.id ? (
            <RecentlyViewedResources />
          ) : null}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="xl:col-span-1 min-w-0">
              <LastDeploy />
            </div>
            <div className="xl:col-span-1 min-w-0">
              <Scaling />
            </div>
          </div>
          <div className="flex flex-col">
            {showOnboardingOnly || org?.id ? <ActivityFeedCompact /> : null}
          </div>
          <SecurityCompliance />
        </div>
      </div>
    </AppSidebarLayout>
  );
};
